import { useEffect } from "react";
import { Link } from "wouter";
import { ArrowLeft, Shield } from "lucide-react";

const MUTED = "#94a3b8";
const BORDER = "rgba(99,102,241,0.15)";
const DIVIDER = { height: "1px", background: "linear-gradient(to right, transparent, rgba(99,102,241,0.25), transparent)" };

function PolicySection({ title, children, testId }: { title: string; children: React.ReactNode; testId?: string }) {
  return (
    <section data-testid={testId}>
      <h2 className="text-base font-semibold mb-3 text-white">{title}</h2>
      <div className="space-y-3 text-base leading-relaxed" style={{ color: MUTED }}>
        {children}
      </div>
    </section>
  );
}

export default function Security() {
  useEffect(() => {
    document.title = "Security Policy | Vexis";
  }, []);

  return (
    <div className="min-h-screen" style={{ backgroundColor: "#05070a", color: "#ffffff" }}>
      <nav className="sticky top-0 z-50" style={{ backgroundColor: "rgba(5,7,10,0.92)", borderBottom: `1px solid ${BORDER}`, backdropFilter: "blur(20px)" }}>
        <div className="container mx-auto px-4 py-3 flex items-center gap-4">
          <Link href="/">
            <button className="flex items-center gap-2 text-sm font-medium transition-colors px-3 py-2 rounded-lg hover:bg-white/5" style={{ color: MUTED }}>
              <ArrowLeft className="w-4 h-4" />
              Back to Home
            </button>
          </Link>
        </div>
      </nav>

      <main className="container mx-auto px-4 py-10 md:py-14 max-w-3xl">
        <div className="mb-10">
          <p className="text-xs font-semibold tracking-[0.2em] uppercase mb-3" style={{ color: "#6366f1" }}>Trust &amp; Safety</p>
          <div className="flex items-center gap-3 mb-2">
            <Shield className="w-6 h-6 text-indigo-400" />
            <h1 className="text-3xl font-bold text-white" data-testid="text-security-title">Security Policy</h1>
          </div>
          <p className="text-sm mt-1" style={{ color: MUTED }}>Last updated: April 2026</p>
        </div>

        <div className="space-y-8 text-base leading-relaxed">
          <PolicySection title="Infrastructure and Data Residency" testId="section-security-infrastructure">
            <p>
              Vexis stores all candidate and recruiter data on Supabase, hosted on AWS infrastructure in the
              ap-southeast-1 region (Singapore). Supabase holds SOC 2 Type 2 certification. Data does not leave
              this region except where explicitly required to provide the service (video processing, AI
              transcription and scoring).
            </p>
          </PolicySection>

          <div style={DIVIDER} />

          <PolicySection title="Encryption" testId="section-security-encryption">
            <p>
              All data is encrypted in transit using TLS 1.2 or higher. All data is encrypted at rest using
              AES-256. Recruiter communication credentials (SendGrid and Twilio API keys) are encrypted using
              AES-256-GCM before storage and decrypted only at the point of use. OAuth tokens are stored in
              environment secrets only and never written to the database.
            </p>
          </PolicySection>

          <div style={DIVIDER} />

          <PolicySection title="Video Processing" testId="section-security-video">
            <p>
              Candidate video recordings are processed by Cloudflare Stream. Video files are never stored on Vexis
              servers. Recordings are automatically deleted within 24 hours of assessment completion regardless of
              any other action. Cloudflare Stream operates on a globally distributed network with SOC 2 Type 2
              certification.
            </p>
          </PolicySection>

          <div style={DIVIDER} />

          <PolicySection title="AI Processing" testId="section-security-ai">
            <p>
              Candidate video responses are transcribed using OpenAI Whisper and scored using OpenAI GPT-4o. Data
              sent to OpenAI is used only for the purpose of providing the Platform and is subject to OpenAI's
              enterprise data processing terms. OpenAI does not use API-submitted data to train models.
            </p>
          </PolicySection>

          <div style={DIVIDER} />

          <PolicySection title="Access Controls" testId="section-security-access">
            <p>
              The Platform operates with three user roles: Administrator (Vexis internal team only), Recruitment
              Agent (recruiters), and Candidate (token-link access only). Candidates do not hold accounts and
              cannot access any part of the Platform beyond their own assessment session. All administrative routes
              are protected by role-based access control enforced at both the application and database level.
            </p>
          </PolicySection>

          <div style={DIVIDER} />

          <PolicySection title="Candidate Data Minimisation" testId="section-security-data-minimisation">
            <p>
              Vexis collects only the data required to provide the assessment service. Candidate video recordings
              are deleted within 24 hours. Transcripts and scores are retained only for the duration of the active
              recruitment process. Candidates may request deletion of their data at any time by contacting{" "}
              <a href="mailto:privacy@vexistalentagency.com" className="text-indigo-400 hover:text-indigo-300 transition-colors">
                privacy@vexistalentagency.com
              </a>
              . Deletion requests are actioned within 30 days.
            </p>
          </PolicySection>

          <div style={DIVIDER} />

          <PolicySection title="Payment Security" testId="section-security-payment">
            <p>
              Payment processing is handled entirely by Stripe. Vexis does not store, process, or transmit card
              numbers, CVV codes, or bank details. All payment data is handled by Stripe in accordance with PCI
              DSS Level 1 compliance.
            </p>
          </PolicySection>

          <div style={DIVIDER} />

          <PolicySection title="Vulnerability Disclosure" testId="section-security-disclosure">
            <p>
              If you discover a security vulnerability in the Vexis platform, please report it responsibly to{" "}
              <a href="mailto:security@vexistalentagency.com" className="text-indigo-400 hover:text-indigo-300 transition-colors">
                security@vexistalentagency.com
              </a>
              . We ask that you do not publicly disclose the vulnerability until we have had a reasonable
              opportunity to investigate and address it. We will acknowledge receipt within 2 business days and
              provide a resolution timeline within 7 business days.
            </p>
          </PolicySection>

          <div style={DIVIDER} />

          <PolicySection title="Compliance" testId="section-security-compliance">
            <p>
              Vexis is designed to support compliance with: New Zealand Privacy Act 2020, Australian Privacy Act
              1988 and Australian Privacy Principles, EU General Data Protection Regulation (GDPR), EU AI Act
              (transparency and human oversight requirements), US Equal Employment Opportunity Commission (EEOC)
              guidelines on AI in hiring.
            </p>
          </PolicySection>

          <div style={DIVIDER} />

          <div className="pt-2">
            <p className="text-sm font-semibold text-white mb-2">Contact</p>
            <p className="text-sm" style={{ color: MUTED }}>
              Security concerns:{" "}
              <a href="mailto:security@vexistalentagency.com" className="text-indigo-400 hover:text-indigo-300 transition-colors">
                security@vexistalentagency.com
              </a>
            </p>
            <p className="text-sm mt-1" style={{ color: MUTED }}>
              Privacy and data requests:{" "}
              <a href="mailto:privacy@vexistalentagency.com" className="text-indigo-400 hover:text-indigo-300 transition-colors">
                privacy@vexistalentagency.com
              </a>
            </p>
          </div>
        </div>
      </main>

      <footer className="py-8 mt-12" style={{ borderTop: `1px solid ${BORDER}` }}>
        <div className="container mx-auto px-4 text-center text-sm" style={{ color: MUTED }}>
          <p>&copy; 2026 Vexis Talent Agency</p>
          <p className="text-xs mt-1" style={{ color: "#64748b" }}>Vexis Talent Agency is a trading name of LHA Holdings Ltd.</p>
          <div className="flex justify-center gap-6 mt-3 flex-wrap">
            <Link href="/privacy" className="hover:text-white transition-colors">Privacy Policy</Link>
            <Link href="/terms" className="hover:text-white transition-colors">Terms of Service</Link>
            <Link href="/security" className="hover:text-white transition-colors">Security</Link>
          </div>
        </div>
      </footer>
    </div>
  );
}
